import { useCallback, useState } from "react";
import {
    getConsolidationErrorMessage,
    useCheckEligibilityMutation,
    useCreateGroupMutation,
    useCreateRelationshipMutation,
    useDeleteRelationshipMutation,
} from "@api/consolidation.api";
import { Entity } from "@common/types/entity";
import { ConsolidationRelationshipResponse } from "@common/schemas/domains/accounting/consolidation.schema";
import { ConsolidationEntityRelationshipType } from "@common/types/domains/accounting/consolidation";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { isActiveRelationship } from "../ownershipGraph.helpers";
import {
    getMultiTierConsolidationBlockedMessage,
    isOwnedByBusinessEntity,
} from "../ownershipProposal.helpers";
import { getConsolidationEligibilityErrorMessage } from "./consolidationEligibilityMessage";

interface ConsolidationSubmissionOptions {
    workspaceId: string;
    parentEntity: Entity | undefined;
    entities: Entity[];
    subsidiaryEntityUuids: string[];
    relationships: ConsolidationRelationshipResponse[];
    ledgerIdsByEntityUuid: Record<string, string>;
    onSuccess: () => void;
}

interface ConsolidationSubmission {
    isSubmitting: boolean;
    submissionError: string | null;
    submit: () => Promise<void>;
    resetSubmission: () => void;
}

export function useConsolidationSubmission({
    workspaceId,
    parentEntity,
    entities,
    subsidiaryEntityUuids,
    relationships,
    ledgerIdsByEntityUuid,
    onSuccess,
}: ConsolidationSubmissionOptions): ConsolidationSubmission {
    const createRelationshipMutation =
        useCreateRelationshipMutation(workspaceId);
    const deleteRelationshipMutation =
        useDeleteRelationshipMutation(workspaceId);
    const checkEligibilityMutation = useCheckEligibilityMutation(workspaceId);
    const createGroupMutation = useCreateGroupMutation(workspaceId);
    const { error: toastError } = useToaster();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submissionError, setSubmissionError] = useState<string | null>(null);

    const resetSubmission = useCallback(() => {
        setIsSubmitting(false);
        setSubmissionError(null);
    }, []);

    const submit = useCallback(async () => {
        if (!parentEntity || isSubmitting) {
            return;
        }
        const parentUuid = parentEntity.uuid;
        setSubmissionError(null);
        setIsSubmitting(true);

        const entitiesByUuid = new Map(
            entities.map((entity) => [entity.uuid, entity]),
        );
        if (
            isOwnedByBusinessEntity({
                entityUuid: parentUuid,
                relationships,
                entitiesByUuid,
            })
        ) {
            const parentName = parentEntity.name?.trim() ?? "This entity";
            toastError(
                getMultiTierConsolidationBlockedMessage(
                    parentName === "" ? "This entity" : parentName,
                ),
            );
            setIsSubmitting(false);
            return;
        }

        // The ownership edges this run creates, so we can roll them back if a
        // later step fails — eligibility needs the edges to exist, so they
        // must be written first, but a partial failure must not leave orphaned
        // 100% edges (they drive consolidation boundary rendering).
        const createdEdgeIds: string[] = [];
        const rollbackEdges = async () => {
            await Promise.allSettled(
                createdEdgeIds.map((id) =>
                    deleteRelationshipMutation.mutateAsync(id),
                ),
            );
        };
        const failToast = async (message: string) => {
            await rollbackEdges();
            toastError(message);
            setIsSubmitting(false);
        };
        const failInline = async (message: string | null) => {
            await rollbackEdges();
            if (message !== null) {
                setSubmissionError(message);
            }
            setIsSubmitting(false);
        };

        // 1. Create the 100% ownership edges that don't already exist, then
        //    confirm eligibility. These mutations suppress the global error
        //    toast, so surface their errors inline (eligibility uses toast).
        try {
            const alreadyOwnedUuids = new Set(
                relationships
                    .filter(
                        (relationship) =>
                            isActiveRelationship(relationship) &&
                            relationship.type ===
                                ConsolidationEntityRelationshipType.ownership &&
                            relationship.sourceEntityUuid === parentUuid,
                    )
                    .map((relationship) => relationship.targetEntityUuid),
            );
            const missingSubsidiaryUuids = subsidiaryEntityUuids.filter(
                (uuid) => !alreadyOwnedUuids.has(uuid),
            );
            for (const targetEntityUuid of missingSubsidiaryUuids) {
                const created = await createRelationshipMutation.mutateAsync({
                    type: ConsolidationEntityRelationshipType.ownership,
                    sourceEntityUuid: parentUuid,
                    targetEntityUuid,
                    ownershipPercentage: 100,
                });
                createdEdgeIds.push(created.id);
            }

            const eligibility = await checkEligibilityMutation.mutateAsync({
                parentEntityUuid: parentUuid,
                memberEntityUuids: subsidiaryEntityUuids,
            });
            if (!eligibility.eligible) {
                await failToast(
                    getConsolidationEligibilityErrorMessage({
                        violations: eligibility.violations,
                        relationships,
                        entities,
                        parentEntityUuid: parentUuid,
                    }),
                );
                return;
            }
        } catch (caught) {
            await failInline(
                getConsolidationErrorMessage(
                    caught,
                    "Failed to create consolidation group",
                ),
            );
            return;
        }

        // 2. Create the group. createGroup surfaces API errors via the global
        //    toast, so roll back the edges and stop the flow on failure.
        try {
            await createGroupMutation.mutateAsync({
                name: parentEntity.name ?? "Consolidation",
                parentEntityUuid: parentUuid,
                memberEntityUuids: subsidiaryEntityUuids,
                config: { ledgerIdsByEntityUuid },
            });
        } catch {
            await failInline(null);
            return;
        }

        setIsSubmitting(false);
        onSuccess();
    }, [
        parentEntity,
        entities,
        ledgerIdsByEntityUuid,
        isSubmitting,
        relationships,
        subsidiaryEntityUuids,
        createRelationshipMutation,
        deleteRelationshipMutation,
        checkEligibilityMutation,
        createGroupMutation,
        toastError,
        onSuccess,
    ]);

    return {
        isSubmitting,
        submissionError,
        submit,
        resetSubmission,
    };
}
